import { FC } from "react"
import { Trash2, ChevronRight, ChevronDown, List } from "lucide-react"
import { ConnectorDataType } from "../../../../../../../types"
import { getFieldDefinition } from "../../../../../../constants/connector-field-types"
import ValueTransformEditor from "./ValueTransformEditor"
import ConnectorFormInput from "../../components/ConnectorFormInput"

interface FieldMappingRowProps {
  stableKey: string
  mapKey: string
  value: any
  isIncoming: boolean
  dataType: ConnectorDataType
  bbField: string
  crmField: string
  isExpanded: boolean
  onUpdateMapping: (
    oldKey: string,
    newBbField: string,
    newCrmField: string
  ) => void
  onRemove: (key: string) => void
  onToggleExpanded: (key: string) => void
  onUpdateValueTransform: (
    key: string,
    sourceValue: string,
    targetValue: string
  ) => void
  onRemoveValueTransform: (key: string, sourceValue: string) => void
  onRemoveAllValueTransforms: (key: string) => void
  onCollapseField: (key: string) => void
  valueTransforms: Record<string, any>
}

const FieldMappingRow: FC<FieldMappingRowProps> = ({
  stableKey,
  mapKey,
  value,
  isIncoming,
  dataType,
  bbField,
  crmField,
  isExpanded,
  onUpdateMapping,
  onRemove,
  onToggleExpanded,
  onUpdateValueTransform,
  onRemoveValueTransform,
  onRemoveAllValueTransforms,
  onCollapseField,
  valueTransforms
}) => {
  const fieldDef = getFieldDefinition(bbField, dataType)
  const isSelectField = fieldDef?.type === "select"
  const hasTransforms = Object.keys(valueTransforms).length > 0

  // Generate human-readable label for custom fields
  const getFieldLabel = (fieldPath: string): string => {
    if (fieldDef?.label) return fieldDef.label

    // For custom fields, convert path to readable label
    // e.g., "details.utm_campaign" -> "Utm Campaign"
    // e.g., "quote_info.custom_field" -> "Custom Field"
    const parts = fieldPath.split(".")
    const fieldName = parts[parts.length - 1] // Get last part after final dot

    return fieldName
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const displayLabel = getFieldLabel(bbField)

  return (
    <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
      <div className="grid grid-cols-6 gap-2">
        {/* Left column */}
        <div className="col-span-2">
          {isIncoming ? (
            <ConnectorFormInput
              label=""
              value={crmField}
              onChange={value => onUpdateMapping(mapKey, bbField, value)}
              placeholder="e.g., homebuyer.email"
            />
          ) : (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 text-base flex items-center">
              {displayLabel}
              {isSelectField && (
                <span className="text-xs text-purple-600 ml-1">(select)</span>
              )}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="col-span-3">
          {isIncoming ? (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 text-base flex items-center">
              {displayLabel}
              {isSelectField && (
                <span className="text-xs text-purple-600 ml-1">(select)</span>
              )}
            </div>
          ) : (
            <ConnectorFormInput
              label=""
              value={crmField}
              onChange={value => onUpdateMapping(mapKey, bbField, value)}
              placeholder="e.g., FirstName or name.first"
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-1">
          <button
            type="button"
            onClick={() => onToggleExpanded(mapKey)}
            className={`p-1 rounded ${
              hasTransforms
                ? "text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                : "text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            }`}
            title={
              hasTransforms
                ? isExpanded
                  ? "Hide value mapping"
                  : "Show value mapping"
                : "Map values"
            }
          >
            {hasTransforms && isExpanded ? (
              <ChevronDown size={16} />
            ) : hasTransforms ? (
              <ChevronRight size={16} />
            ) : (
              <List size={16} />
            )}
          </button>
          <button
            type="button"
            onClick={() => onRemove(mapKey)}
            className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
            title="Remove field"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Value transformation mapping */}
      {isExpanded && (
        <ValueTransformEditor
          key={mapKey}
          mapKey={mapKey}
          isIncoming={isIncoming}
          isSelectField={isSelectField}
          fieldDef={fieldDef}
          valueTransforms={valueTransforms}
          hasTransforms={hasTransforms}
          onUpdateValueTransform={onUpdateValueTransform}
          onRemoveValueTransform={onRemoveValueTransform}
          onRemoveAllValueTransforms={onRemoveAllValueTransforms}
          onCollapseField={onCollapseField}
        />
      )}
    </div>
  )
}

export default FieldMappingRow
